/**
 * Large video uploads: browser PUTs ~10 MiB parts directly to S3 using presigned URLs.
 * Requires the S3 bucket CORS rule to allow PUT from your app origin and ExposeHeader: ETag.
 */

import { PHOTOS } from '@/shared/constants/backend-api'
import type { Photo } from '@/features/photos/actions'
import type { BasePhoto } from '@/features/base/types/base-content'
import {
  DEFAULT_UPLOAD_BASE_DELAY_MS,
  DEFAULT_UPLOAD_MAX_ATTEMPTS,
  executeWithUploadRetries,
  fetchUploadBearerToken,
  defaultUploadShouldRetry,
  isErrorWithStatus,
} from '@/shared/lib/upload-transient-retry'

/** Use S3 multipart for home/base videos at or above this size (parts are ~10 MiB from the backend). */
export const VIDEO_MULTIPART_MIN_FILE_BYTES = 10 * 1024 * 1024

function shouldRetryApiError(error: unknown, attemptIndex: number): boolean {
  if (isErrorWithStatus(error)) {
    const s = error.status ?? 0
    if (s === 400 || s === 403 || s === 404 || s === 413 || s === 422) return false
  }
  return defaultUploadShouldRetry(error, attemptIndex)
}

function extractMessage(json: unknown): string | undefined {
  if (!json || typeof json !== 'object') return undefined
  const rec = json as Record<string, unknown>
  if (typeof rec.message === 'string') return rec.message
  return undefined
}

async function postMultipartJson<T>(url: string, body: Record<string, unknown>): Promise<T> {
  return executeWithUploadRetries(
    async () => {
      const token = await fetchUploadBearerToken()
      if (!token) {
        throw Object.assign(new Error('Not authenticated'), { status: 401 })
      }
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })
      const json = (await res.json().catch(() => ({}))) as Record<string, unknown>
      if (!res.ok) {
        const msg =
          extractMessage(json) ||
          (res.status === 413
            ? 'Upload too large.'
            : `HTTP error! status: ${res.status}`)
        throw Object.assign(new Error(msg), { status: res.status })
      }
      if (json.success !== true) {
        const msg = extractMessage(json) || 'Request failed'
        throw Object.assign(new Error(msg), { status: res.status })
      }
      return json.data as T
    },
    shouldRetryApiError,
    { maxAttempts: DEFAULT_UPLOAD_MAX_ATTEMPTS, baseDelayMs: DEFAULT_UPLOAD_BASE_DELAY_MS }
  )
}

function putS3PartOnce(
  presignedUrl: string,
  blob: Blob,
  onPartByteProgress: (loadedInPart: number) => void
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', presignedUrl)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onPartByteProgress(e.loaded)
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader('ETag')
        if (!etag) {
          reject(
            new Error(
              'S3 multipart: missing ETag. Add "ETag" to ExposeHeaders in your bucket CORS configuration.'
            )
          )
          return
        }
        resolve(etag)
        return
      }
      reject(
        Object.assign(new Error(`S3 part upload failed (HTTP ${xhr.status})`), {
          status: xhr.status,
        })
      )
    }
    xhr.onerror = () =>
      reject(
        new TypeError(
          'Video part upload failed: usually S3 CORS (allow this site origin, PUT, expose ETag) or network. Set NEXT_PUBLIC_DISABLE_VIDEO_MULTIPART=true to fall back to single-request uploads.'
        )
      )
    xhr.ontimeout = () => reject(new Error('S3 part upload timed out'))
    xhr.send(blob)
  })
}

async function putS3PartWithRetries(
  presignedUrl: string,
  blob: Blob,
  onPartByteProgress: (loadedInPart: number) => void
): Promise<string> {
  return executeWithUploadRetries(
    async () => putS3PartOnce(presignedUrl, blob, onPartByteProgress),
    shouldRetryApiError,
    { maxAttempts: DEFAULT_UPLOAD_MAX_ATTEMPTS, baseDelayMs: DEFAULT_UPLOAD_BASE_DELAY_MS }
  )
}

export type VideoChunkProgress = {
  uploadedBytes: number
  fileSize: number
  partNumber: number
  totalParts: number
}

export async function uploadHomeVideoMultipartChunks(
  homeId: number,
  file: File,
  opts: {
    workOrderId?: number
    tags?: string[]
    description?: string
  },
  onChunkProgress?: (p: VideoChunkProgress) => void
): Promise<Photo> {
  const contentType =
    file.type ||
    (file.name.toLowerCase().endsWith('.mov') ? 'video/quicktime' : 'video/mp4')

  const init = await postMultipartJson<{
    sessionId: string
    partSizeBytes: number
    totalParts: number
  }>(PHOTOS.videoMultipartHomeInit, {
    filename: file.name,
    contentType,
    fileSize: file.size,
    homeId,
    ...(opts.workOrderId !== undefined ? { workOrderId: opts.workOrderId } : {}),
    ...(opts.description ? { description: opts.description } : {}),
    ...(opts.tags?.length ? { tags: opts.tags } : {}),
  })

  let bytesBeforeCurrentPart = 0

  for (let p = 1; p <= init.totalParts; p++) {
    const start = (p - 1) * init.partSizeBytes
    const end = Math.min(start + init.partSizeBytes, file.size)
    const blob = file.slice(start, end)

    const { url } = await postMultipartJson<{ url: string }>(PHOTOS.videoMultipartSignPart, {
      sessionId: init.sessionId,
      partNumber: p,
    })

    const etag = await putS3PartWithRetries(url, blob, (loadedInPart) => {
      onChunkProgress?.({
        uploadedBytes: bytesBeforeCurrentPart + loadedInPart,
        fileSize: file.size,
        partNumber: p,
        totalParts: init.totalParts,
      })
    })

    await postMultipartJson(PHOTOS.videoMultipartRegisterPart, {
      sessionId: init.sessionId,
      partNumber: p,
      etag,
    })

    bytesBeforeCurrentPart += blob.size
  }

  onChunkProgress?.({
    uploadedBytes: file.size,
    fileSize: file.size,
    partNumber: init.totalParts,
    totalParts: init.totalParts,
  })

  return postMultipartJson<Photo>(PHOTOS.videoMultipartComplete, {
    sessionId: init.sessionId,
  })
}

export type BaseVideoMultipartFinalizeKind = 'timeline' | 'pretrip' | 'hub_about'

export async function uploadBaseVideoMultipartChunks(
  regionId: number,
  file: File,
  opts: {
    description?: string
    takenAt?: string
    componentName?: string
    /** Default timeline Base Photos; use pretrip / hub_about for those Hub tabs (large videos). */
    baseFinalizeKind?: BaseVideoMultipartFinalizeKind
    hubAboutSlotKey?: string
    keepCustomPoster?: boolean
  },
  onChunkProgress?: (p: VideoChunkProgress) => void
): Promise<BasePhoto> {
  const contentType =
    file.type ||
    (file.name.toLowerCase().endsWith('.mov') ? 'video/quicktime' : 'video/mp4')

  const finalizeKind = opts.baseFinalizeKind ?? 'timeline'

  const initBody: Record<string, unknown> = {
    filename: file.name,
    contentType,
    fileSize: file.size,
    regionId,
    ...(opts.description ? { description: opts.description } : {}),
    ...(opts.takenAt ? { takenAt: opts.takenAt } : {}),
    ...(opts.componentName ? { componentName: opts.componentName } : {}),
    ...(finalizeKind !== 'timeline' ? { baseFinalizeKind: finalizeKind } : {}),
    ...(finalizeKind === 'hub_about' && opts.hubAboutSlotKey
      ? { hubAboutSlotKey: opts.hubAboutSlotKey }
      : {}),
    ...(opts.keepCustomPoster ? { keepCustomPoster: true } : {}),
  }

  const init = await postMultipartJson<{
    sessionId: string
    partSizeBytes: number
    totalParts: number
  }>(PHOTOS.videoMultipartBaseInit, initBody)

  let bytesBeforeCurrentPart = 0

  for (let p = 1; p <= init.totalParts; p++) {
    const start = (p - 1) * init.partSizeBytes
    const end = Math.min(start + init.partSizeBytes, file.size)
    const blob = file.slice(start, end)

    const { url } = await postMultipartJson<{ url: string }>(PHOTOS.videoMultipartSignPart, {
      sessionId: init.sessionId,
      partNumber: p,
    })

    const etag = await putS3PartWithRetries(url, blob, (loadedInPart) => {
      onChunkProgress?.({
        uploadedBytes: bytesBeforeCurrentPart + loadedInPart,
        fileSize: file.size,
        partNumber: p,
        totalParts: init.totalParts,
      })
    })

    await postMultipartJson(PHOTOS.videoMultipartRegisterPart, {
      sessionId: init.sessionId,
      partNumber: p,
      etag,
    })

    bytesBeforeCurrentPart += blob.size
  }

  onChunkProgress?.({
    uploadedBytes: file.size,
    fileSize: file.size,
    partNumber: init.totalParts,
    totalParts: init.totalParts,
  })

  return postMultipartJson<BasePhoto>(PHOTOS.videoMultipartComplete, {
    sessionId: init.sessionId,
  })
}

/**
 * Large videos use browser → S3 multipart PUTs. Requires bucket CORS (origin, PUT, expose ETag).
 * Opt out entirely with NEXT_PUBLIC_DISABLE_VIDEO_MULTIPART=true.
 */
export function shouldUseVideoMultipartUpload(file: File): boolean {
  if (!file.type.startsWith('video/')) return false
  if (file.size < VIDEO_MULTIPART_MIN_FILE_BYTES) return false
  if (process.env.NEXT_PUBLIC_DISABLE_VIDEO_MULTIPART === 'true') return false
  return true
}
